/**
 * custom-search.js
 *
 * Custom element <custom-search> that replaces Shopify's native search with
 * a fully custom backend-powered search. Reads filter options from shop
 * metafields (rendered as static HTML by main-custom-search.liquid) and
 * fetches products + filter counts from the backend API.
 *
 * Backend endpoint: {backendUrl}/p_api/search
 * URL format matches Shopify's native search for back-navigation compatibility.
 */

/** Maps backend filterCounts keys → display label + URL param name */
const CS_FILTER_CONFIG = [
  { key: 'utilizzo',                   label: 'Utilizzo',                    param: 'filter.p.m.custom.utilizzo' },
  { key: 'vendor',                     label: 'Brand',                       param: 'filter.p.vendor' },
  { key: 'carico',                     label: 'Carico',                      param: 'filter.p.m.custom.carico' },
  { key: 'codvelocita',                label: 'Codice velocità',             param: 'filter.p.m.custom.codvelocita' },
  { key: 'bordino_protezione_cerchio', label: 'Bordino protezione cerchio',  param: 'filter.p.m.custom.bordino_protezione_cerchio' },
  { key: 'aderenza',                   label: 'Aderenza',                    param: 'filter.p.m.custom.aderenza' },
  { key: 'classe_rumorosita',          label: 'Classe rumorosità',           param: 'filter.p.m.custom.classe_rumorosita' },
  { key: 'consumo',                    label: 'Consumo',                     param: 'filter.p.m.custom.consumo' },
  { key: 'rumorosita_in_db',           label: 'Rumorosità in dB',            param: 'filter.p.m.custom.rumorosita_in_db' },
  { key: 'specifica',                  label: 'Specifica',                   param: 'filter.p.m.custom.specifica' },
  { key: 'tecnologia_antiforatura',    label: 'Tecnologia antiforatura',     param: 'filter.p.m.custom.tecnologia_antiforatura' },
]

const CS_ARROW_ICON = `<svg focusable="false" class="icon icon--arrow-bottom" viewBox="0 0 12 8" role="presentation"><path stroke="currentColor" stroke-width="2" d="M10 2L6 6 2 2" fill="none" stroke-linecap="square"></path></svg>`
const CS_CHECK_ICON = `<svg focusable="false" class="icon icon--check" viewBox="0 0 24 24" role="presentation"><path fill="currentColor" d="M9 20l-7-7 3-3 4 4L19 4l3 3z"></path></svg>`

const CS_PLACEHOLDER_IMG =
  'https://cdn.shopify.com/s/files/1/0648/0943/5364/files/placeholder.jpg?v=1758701736'

const CS_SORT_LABELS = {
  'title-ascending': 'A-Z',
  'price-ascending': 'Prezzo crescente',
  'price-descending': 'Prezzo decrescente',
}

const CS_PERPAGE_LABEL = (n) => `${n} per pagina`

if (!customElements.get('custom-search')) {
  customElements.define(
    'custom-search',
    class CustomSearch extends HTMLElement {
      // ─────────────────────────────────────────────────────────────
      // CONSTRUCTOR — parse URL state
      // ─────────────────────────────────────────────────────────────
      constructor() {
        super()

        const params = new URLSearchParams(window.location.search)

        this.state = {
          q: params.get('q') ?? '',
          sortBy: params.get('sort_by') ?? 'title-ascending',
          page: parseInt(params.get('page') ?? '1', 10),
          perPage: parseInt(params.get('per_page') ?? '24', 10),
          priceGte: params.get('filter.v.price.gte') ?? '',
          priceLte: params.get('filter.v.price.lte') ?? '',
          /** { 'filter.p.m.custom.utilizzo': ['ESTIVO', '4 STAGIONI'], ... } */
          filters: {},
        }

        // Collect all filter.p.* multi-value params from the URL
        for (const [key, value] of params.entries()) {
          if (key.startsWith('filter.p.')) {
            if (!this.state.filters[key]) this.state.filters[key] = []
            this.state.filters[key].push(value)
          }
        }

        this.backendUrl = this.dataset.backendUrl ?? 'http://localhost:8089'
        this.shopDomain = this.dataset.shopDomain ?? ''
        // this.shopDomain = 'gommeaprezzo.myshopify.com';
        this.productTypes = JSON.parse(this.dataset.productTypes ?? '[]')

        this._priceDebounceTimer = null
        this._layoutMode = 'grid'
        this._allPickersClosed = this._allPickersClosed.bind(this)

        console.debug('[custom-search] constructor — initial state', JSON.parse(JSON.stringify(this.state)))
      }

      // ─────────────────────────────────────────────────────────────
      // CONNECTED CALLBACK — wire DOM refs + bind events + initial fetch
      // ─────────────────────────────────────────────────────────────
      connectedCallback() {
        console.log('[custom-search] connectedCallback — backend:', this.backendUrl, 'shop:', this.shopDomain)

        // Products + pagination containers
        this.productsGrid = this.querySelector('[data-products-grid]')
        this.paginationEl = this.querySelector('[data-pagination]')

        // Showing count spans (desktop + mobile header)
        this.showingCountEls = this.querySelectorAll('[data-showing-count]')

        // Active filter chip container (desktop sidebar)
        this.activeFiltersEl = this.querySelector('[data-active-filters]')

        // Mobile filters recap (below toolbar on mobile)
        this.mobileFiltersRecapEl = this.querySelector('[data-cs-mobile-filters-recap]')

        // Mobile filter button parts
        this.mobileFilterIconEl = this.querySelector('[data-cs-filter-icon]')
        this.mobileFilterLabelEl = this.querySelector('[data-cs-filter-label]')

        // Mobile filter drawer
        this.mobileDrawer = this.querySelector('#cs-mobile-filters')
        this.mobileFilterCountEl = this.querySelector('[data-mobile-filter-count]')

        // Sort / per-page labels + page title
        this.sortLabelEl = this.querySelector('[data-cs-sort-label]')
        this.perpageLabelEl = this.querySelector('[data-cs-perpage-label]')
        this.titleEl = this.querySelector('[data-cs-title]')

        // Breadcrumb term span (outside custom-search, in the section)
        this.breadcrumbTermEl = document.querySelector('[data-cs-breadcrumb-term]')

        // Article/page results container
        this.linkResultsEl = this.querySelector('[data-cs-link-results]')

        // Restore UI state from parsed URL params
        this._restoreSortPickerState()
        this._restorePerPagePickerState()
        this._restorePriceInputState()

        // Bind all events (checkboxes + collapsibles use event delegation — work for dynamically rendered groups too)
        this._bindCollapsibleToggles()
        this._bindFilterCheckboxes()
        this._bindPriceInputs()
        this._bindSortPicker()
        this._bindPerPagePicker()
        this._bindValuePickerOpenClose()
        this._bindMobileDrawer()
        this._bindPagination()
        this._bindLayoutSwitcher()

        // Kick off initial product fetch
        this.fetchResults()
      }

      // ─────────────────────────────────────────────────────────────
      // STATE → URL SYNC
      // ─────────────────────────────────────────────────────────────
      syncToURL() {
        const params = new URLSearchParams()

        if (this.state.q) params.set('q', this.state.q)
        params.set('type', 'product')
        if (this.state.sortBy !== 'title-ascending') params.set('sort_by', this.state.sortBy)
        if (this.state.page > 1) params.set('page', String(this.state.page))
        if (this.state.perPage !== 24) params.set('per_page', String(this.state.perPage))
        if (this.state.priceGte) params.set('filter.v.price.gte', this.state.priceGte)
        if (this.state.priceLte) params.set('filter.v.price.lte', this.state.priceLte)

        for (const [key, values] of Object.entries(this.state.filters)) {
          values.forEach((v) => params.append(key, v))
        }

        const newUrl = `${window.location.pathname}?${params.toString()}`
        history.pushState({}, '', newUrl)
        console.debug('[custom-search] syncToURL →', newUrl)
      }

      buildFetchParams() {
        const params = new URLSearchParams()

        if (this.state.q) params.set('q', this.state.q)
        params.set('type', 'product')
        params.set('sort_by', this.state.sortBy)
        params.set('page', String(this.state.page))
        params.set('per_page', String(this.state.perPage))
        if (this.state.priceGte) params.set('filter.v.price.gte', this.state.priceGte)
        if (this.state.priceLte) params.set('filter.v.price.lte', this.state.priceLte)

        for (const [key, values] of Object.entries(this.state.filters)) {
          values.forEach((v) => params.append(key, v))
        }

        return params
      }

      // ─────────────────────────────────────────────────────────────
      // FETCH RESULTS (Tasks 3 + 4)
      // ─────────────────────────────────────────────────────────────
      async fetchResults() {
        console.log('[custom-search] fetchResults — state:', JSON.parse(JSON.stringify(this.state)))
        this._setLoadingState(true)

        try {
          const params = this.buildFetchParams()
          const url = `${this.backendUrl}/p_api/search?${params.toString()}`
          console.debug('[custom-search] fetching URL:', url)

          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'shop-domain': this.shopDomain,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ productTypes: this.productTypes }),
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`)

          const data = await res.json()
          console.log('[custom-search] response received —', {
            totalCount: data.pagination?.totalCount,
            currentPage: data.pagination?.currentPage,
            productsCount: data.products?.length,
          })
          console.debug('[custom-search] full response data:', data)

          this._renderProducts(data.products ?? [])
          this._renderPagination(data.pagination ?? {})
          this._renderFilterOptions(data.filterCounts ?? {})
          this._renderActiveFilters()
          this._updateShowingCount(data.pagination)
          this._updateTitle()
          this._loadContentResults()
        } catch (err) {
          console.error('[custom-search] fetchResults error:', err)
          this._renderError()
        } finally {
          this._setLoadingState(false)
        }
      }

      // ─────────────────────────────────────────────────────────────
      // RENDER: PRODUCTS
      // ─────────────────────────────────────────────────────────────
      _renderProducts(products) {
        console.debug('[custom-search] _renderProducts — count:', products.length)

        if (!products.length) {
          this.productsGrid.innerHTML = `
            <div class="empty-state" style="width:100%">
              <p class="empty-state__description">Nessun prodotto trovato per questa ricerca.</p>
            </div>`
          return
        }

        this.productsGrid.innerHTML = products
          .map((product) => this._productCardTemplate(product))
          .join('')
      }

      _productCardTemplate(product) {
        const rawPrice = Number(product.price ?? product.priceRange?.minVariantPrice?.amount ?? 0)
        // Prices stored as cents (e.g. 9739.0 = €97.39) — divide by 100, then add VAT
        const priceEuros = rawPrice > 1000 ? rawPrice / 100 : rawPrice
        const priceWithVat = priceEuros * 1.22
        const priceDisplay = `€${priceWithVat.toFixed(2)} EUR`

        const imageUrl = product.image ?? product.featuredImage?.url ?? CS_PLACEHOLDER_IMG
        const imageAlt = product.imageAlt ?? product.featuredImage?.altText ?? product.title ?? ''
        const productUrl = product.onlineStoreUrl ?? `/products/${product.handle}`
        const vendor = product.vendor ?? ''

        // Brand logo (from window.elsBrandsLogos metafield map)
        const brandLogoUrl = window.elsBrandsLogos?.[vendor]
        const brandLogoHtml = brandLogoUrl
          ? `<div class="product-item__brand"><img src="${brandLogoUrl}" alt="${this._escapeHtml(vendor)}" class="product-item__brand-logo" loading="lazy"></div>`
          : ''

        // Inventory badge
        const inv = product.totalInventory ?? 0
        let inventoryHtml = ''
        if (inv <= 0) {
          // {{ 'collection.product.sold_out' | t }} equivalent
          inventoryHtml = `<span class="product-item__inventory inventory product-item__availability product-item__availability--sold-out">Out of stock</span>`
        } else {
          // {{ 'collection.product.in_stock' | t }} equivalent
          // Previously: Disponibilità limitata (${inv}) / Disponibile (${inv}) — kept below for reference
          // inventoryHtml = inv <= 5
          //   ? `<span class="product-item__inventory inventory inventory--low">Disponibilità limitata (${inv})</span>`
          //   : `<span class="product-item__inventory inventory inventory--high">Disponibile (${inv})</span>`
          inventoryHtml = `<span class="product-item__inventory inventory product-item__availability product-item__availability--in-stock">In stock</span>`
        }

        // ATC button — extract numeric variant ID from GID (e.g. "gid://shopify/ProductVariant/123" → "123")
        const numericVariantId = product.selectedVariantId?.split('/')?.pop() ?? ''
        const atcHtml = inv <= 0
          ? `<div class="product-item__action-list button-stack">
               <button type="button" class="product-item__action-button button button--small button--primary" disabled>Esaurito</button>
             </div>`
          : `<div class="product-item__action-list button-stack">
               <form method="post" action="/cart/add" class="cs-atc-form">
                 <input type="hidden" name="id" value="${numericVariantId}">
                 <input type="hidden" name="quantity" value="1">
                 <buy-button>
                   <button type="submit" class="product-item__action-button button button--small button--primary">Aggiungi al carrello</button>
                 </buy-button>
               </form>
             </div>`

        return `
          <div class="${this._layoutMode === 'list' ? 'product-item product-item--list' : 'product-item product-item--vertical 1/3--tablet-and-up 1/4--desk'}">
            ${brandLogoHtml}
            <a href="${productUrl}" class="product-item__image-wrapper">
              <div class="aspect-ratio aspect-ratio--square">
                <img
                  src="${imageUrl}"
                  alt="${this._escapeHtml(imageAlt)}"
                  class="product-item__primary-image"
                  loading="lazy"
                  width="400"
                  height="400"
                  onerror="this.onerror=null;this.src='${CS_PLACEHOLDER_IMG}'"
                >
              </div>
            </a>
            <div class="product-item__info">
              ${vendor ? `<p class="product-item__vendor text--subdued">${this._escapeHtml(vendor)}</p>` : ''}
              <p class="product-item__title text--strong">
                <a href="${productUrl}">${this._escapeHtml(product.title ?? '')}</a>
              </p>
              <div class="product-item__price-list price-list">
                <span class="price price--highlight">${priceDisplay}</span>
              </div>
              ${inventoryHtml}
            </div>
            ${atcHtml}
          </div>`
      }

      _escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
      }

      // ─────────────────────────────────────────────────────────────
      // RENDER: PAGINATION
      // ─────────────────────────────────────────────────────────────
      _renderPagination(pagination) {
        const { totalPages = 0, currentPage = 1 } = pagination
        console.debug('[custom-search] _renderPagination — totalPages:', totalPages, 'current:', currentPage)

        if (totalPages <= 1) {
          this.paginationEl.innerHTML = ''
          return
        }

        const current = Number(currentPage)
        const last = Number(totalPages)
        const delta = 2
        const left = current - delta
        const right = current + delta + 1
        const range = []
        const rangeWithDots = []
        let l

        for (let i = 1; i <= last; i++) {
          if (i === 1 || i === last || (i >= left && i < right)) range.push(i)
        }

        for (const i of range) {
          if (l) {
            if (i - l === 2) rangeWithDots.push(l + 1)
            else if (i - l !== 1) rangeWithDots.push('...')
          }
          rangeWithDots.push(i)
          l = i
        }

        const navItems = rangeWithDots
          .map((item) =>
            item === '...'
              ? `<span class="pagination__nav-item">${item}</span>`
              : `<span
                  data-page="${item}"
                  class="pagination__nav-item ${current === item ? 'is-active' : ''}"
                  ${current !== item ? 'style="cursor:pointer;"' : ''}
                >${item}</span>`,
          )
          .join('')

        this.paginationEl.innerHTML = `
          <div class="pagination">
            <div class="pagination__inner">
              ${current > 1 ? `<a class="pagination__prev link" data-page="${current - 1}" style="cursor:pointer;">← Precedente</a>` : ''}
              <div class="pagination__nav">
                ${navItems}
              </div>
              <span class="pagination__page-count text--subdued">Pagina ${current} di ${last}</span>
              ${current < last ? `<a class="pagination__next link" data-page="${current + 1}" style="cursor:pointer;">Successiva →</a>` : ''}
            </div>
          </div>`
      }

      // ─────────────────────────────────────────────────────────────
      // RENDER: FILTER OPTIONS FROM BACKEND (replaces static metafield HTML)
      // ─────────────────────────────────────────────────────────────
      /**
       * Renders filter group checkboxes into [data-filter-groups-desktop] and
       * [data-filter-groups-mobile] based on filterCounts returned by the backend.
       * Only shows filter groups that have at least one value for the current query.
       * Restores checked state from this.state.filters.
       */
      _renderFilterOptions(filterCounts) {
        console.debug('[custom-search] _renderFilterOptions —', filterCounts)

        const desktopContainer = this.querySelector('[data-filter-groups-desktop]')
        const mobileContainer = this.querySelector('[data-filter-groups-mobile]')
        if (!desktopContainer && !mobileContainer) return

        const buildGroupHtml = (key, label, param, open) => {
          const counts = filterCounts[key]
          if (!counts || Object.keys(counts).length === 0) return ''

          const entries = Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]))

          const items = entries.map(([value, count]) => {
            const isChecked = !!(this.state.filters[param]?.includes(value))
            const cbId = `cs-cb-${key}-${value}`.replace(/[^a-z0-9-]/gi, '_')
            return `
              <li class="collection__filter-checkbox">
                <div class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="${cbId}"
                    class="checkbox"
                    name="${this._escapeHtml(param)}"
                    value="${this._escapeHtml(value)}"
                    ${isChecked ? 'checked' : ''}
                  >
                  ${CS_CHECK_ICON}
                </div>
                <label for="${cbId}">${this._escapeHtml(value)} <span class="cs-filter-count">(${count})</span></label>
              </li>`
          }).join('')

          return `
            <div class="collection__filter-group">
              <button
                type="button"
                class="collection__filter-group-name link link--secondary text--strong"
                data-cs-collapsible-toggle
                aria-expanded="${open ? 'true' : 'false'}"
              >
                <div style="display: flex; flex-direction: column;">
                 ${this._escapeHtml(label)}
                </div>
                ${CS_ARROW_ICON}
              </button>
              <div class="collection__filter-collapsible" aria-hidden="${open ? 'false' : 'true'}" ${open ? 'style="height: auto; overflow: visible;"' : ''}>
                <ul class="collection__filter-checkbox-list" role="list">
                  ${items}
                </ul>
              </div>
            </div>`
        }

        if (desktopContainer) {
          desktopContainer.innerHTML = CS_FILTER_CONFIG
            .map(({ key, label, param }) => buildGroupHtml(key, label, param, true))
            .join('')
        }
        if (mobileContainer) {
          mobileContainer.innerHTML = CS_FILTER_CONFIG
            .map(({ key, label, param }) => buildGroupHtml(key, label, param, false))
            .join('')
        }

        console.log('[custom-search] _renderFilterOptions — groups rendered:', CS_FILTER_CONFIG.filter(({ key }) => !!filterCounts[key]).length)
      }

      // ─────────────────────────────────────────────────────────────
      // RENDER: ACTIVE FILTER CHIPS (Task 4)
      // ─────────────────────────────────────────────────────────────
      _renderActiveFilters() {
        const chips = this._buildActiveFilterChips()
        console.log('[custom-search] _renderActiveFilters — active chips:', chips.length)

        const chipsHtml = chips.length
          ? `${chips.join('')}
             <button class="collection__clear-filter button button--small button--transparent button--full" data-cs-clear-all>
               Pulisci tutto
             </button>`
          : ''

        // Desktop sidebar active filters
        if (this.activeFiltersEl) this.activeFiltersEl.innerHTML = chipsHtml

        // ── Mobile: filters recap below toolbar (not in drawer) ──
        if (this.mobileFiltersRecapEl) {
          if (chips.length) {
            // Build mobile recap chips (same style as reference: collection__mobile-active-filter-item)
            const mobileChips = this._buildMobileRecapChips()
            this.mobileFiltersRecapEl.innerHTML = `
              <div class="collection__mobile-active-filters">
                ${mobileChips.join('')}
                ${chips.length > 1 ? `<button class="collection__mobile-active-clear" data-cs-clear-all>Pulisci tutto</button>` : ''}
              </div>`
            this.mobileFiltersRecapEl.style.display = ''
          } else {
            this.mobileFiltersRecapEl.innerHTML = ''
            this.mobileFiltersRecapEl.style.display = 'none'
          }
        }

        // ── Mobile filter button: count + active icon indicator ──
        if (this.mobileFilterLabelEl) {
          this.mobileFilterLabelEl.innerHTML = chips.length
            ? `Filtro&nbsp;(${chips.length})`
            : 'Filtri'
        }
        if (this.mobileFilterIconEl) {
          this.mobileFilterIconEl.classList.toggle('collection__filter-icon--active', chips.length > 0)
        }

        // Show/hide drawer header clear button
        const drawerClearBtn = this.mobileDrawer?.querySelector('[data-cs-clear-all]')
        if (drawerClearBtn) drawerClearBtn.style.display = chips.length ? '' : 'none'

        // ── Drawer: add collection__filter-item-active text to group buttons ──
        this._updateDrawerFilterItemActive()

        // Re-bind clear-all buttons
        this.querySelectorAll('[data-cs-clear-all]').forEach((btn) => {
          btn.addEventListener('click', () => this.clearAllFilters())
        })

        // Re-bind individual chip remove buttons
        this.querySelectorAll('[data-cs-remove-filter]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.filterKey
            const value = btn.dataset.filterValue
            if (key === '__price__') {
              this.state.priceGte = ''
              this.state.priceLte = ''
              this._syncPriceInputs()
            } else {
              this._removeFilter(key, value)
              return
            }
            this.state.page = 1
            this.syncToURL()
            this.fetchResults()
          })
        })
      }

      _buildMobileRecapChips() {
        const chips = []

        if (this.state.priceGte || this.state.priceLte) {
          const label = `${this.state.priceGte ? `€${this.state.priceGte}` : '0'} – ${this.state.priceLte ? `€${this.state.priceLte}` : 'max'}`
          chips.push(`
            <button class="collection__mobile-active-filter-item" data-cs-remove-filter data-filter-key="__price__" data-filter-value="">
              <span class="collection__mobile-active-filter-cross">${this._closeIconSvg()}</span>
              ${label}
            </button>`)
        }

        for (const [param, values] of Object.entries(this.state.filters)) {
          for (const value of values) {
            chips.push(`
              <button class="collection__mobile-active-filter-item" data-cs-remove-filter data-filter-key="${this._escapeHtml(param)}" data-filter-value="${this._escapeHtml(value)}">
                <span class="collection__mobile-active-filter-cross">${this._closeIconSvg()}</span>
                ${this._escapeHtml(value)}
              </button>`)
          }
        }

        return chips
      }

      _updateDrawerFilterItemActive() {
        const mobileContainer = this.querySelector('[data-filter-groups-mobile]')
        if (!mobileContainer) return

        // For each rendered filter group button in drawer, add/remove active text
        mobileContainer.querySelectorAll('[data-cs-collapsible-toggle]').forEach((btn) => {
          // The input name tells us which param this group uses
          const firstCheckbox = btn.nextElementSibling?.querySelector('input[type="checkbox"]')
          if (!firstCheckbox) return
          const param = firstCheckbox.name

          const activeValues = this.state.filters[param] ?? []

          // Remove existing active span if present
          const existingSpan = btn.querySelector('.collection__filter-item-active')
          if (existingSpan) existingSpan.remove()

          if (activeValues.length) {
            const span = document.createElement('span')
            span.className = 'collection__filter-item-active'
            span.textContent = activeValues.join(', ')
            // Insert before the arrow icon (last child)
            btn.querySelector('div').appendChild(span)
            // insertBefore(span, btn.lastElementChild)
          }
        })
      }

      _buildActiveFilterChips() {
        const chips = []

        // Price range chip
        if (this.state.priceGte || this.state.priceLte) {
          const label = `${this.state.priceGte ? `€${this.state.priceGte}` : '0'} – ${this.state.priceLte ? `€${this.state.priceLte}` : 'max'}`
          chips.push(`
            <button
              class="collection__active-filter-item"
              data-cs-remove-filter
              data-filter-key="__price__"
              data-filter-value=""
            >
              <span class="collection__active-filter-cross">${this._closeIconSvg()}</span>
              ${label}
            </button>`)
        }

        // Filter value chips
        for (const [param, values] of Object.entries(this.state.filters)) {
          for (const value of values) {
            chips.push(`
              <button
                class="collection__active-filter-item"
                data-cs-remove-filter
                data-filter-key="${this._escapeHtml(param)}"
                data-filter-value="${this._escapeHtml(value)}"
              >
                <span class="collection__active-filter-cross">${this._closeIconSvg()}</span>
                ${this._escapeHtml(value)}
              </button>`)
          }
        }

        return chips
      }

      _closeIconSvg() {
        return `<svg aria-hidden="true" focusable="false" class="icon icon--close" viewBox="0 0 24 24">
          <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>`
      }

      _removeFilter(key, value) {
        if (!this.state.filters[key]) return
        this.state.filters[key] = this.state.filters[key].filter((v) => v !== value)
        if (!this.state.filters[key].length) delete this.state.filters[key]
        this.state.page = 1
        console.log('[custom-search] _removeFilter —', key, value)
        this.syncToURL()
        this.fetchResults()
      }

      clearAllFilters() {
        console.log('[custom-search] clearAllFilters')
        this.state.filters = {}
        this.state.priceGte = ''
        this.state.priceLte = ''
        this.state.page = 1
        this._syncPriceInputs()
        this.syncToURL()
        this.fetchResults()
      }

      // ─────────────────────────────────────────────────────────────
      // SHOWING COUNT + LOADING + ERROR
      // ─────────────────────────────────────────────────────────────
      _updateShowingCount(pagination) {
        if (!pagination) return
        const { totalCount = 0, currentPage = 1, perPage = this.state.perPage } = pagination
        const from = (currentPage - 1) * perPage + 1
        const to = Math.min(currentPage * perPage, totalCount)
        const text = totalCount > 0
          ? `Visualizzati ${from} - ${to} di ${totalCount} risultati`
          : 'Nessun risultato'

        console.debug('[custom-search] _updateShowingCount —', text)
        this.showingCountEls.forEach((el) => { el.textContent = text })
      }

      _updateTitle() {
        if (this.titleEl) {
          const displayQ = (this.state.q ?? '').replace(/product_type:/gi, '').trim()
          this.titleEl.textContent = displayQ
            ? `Prodotti per il termine "${displayQ}"`
            : 'Risultati ricerca'
        }

        // Update breadcrumb last item with current search term
        if (this.breadcrumbTermEl) {
          const span = this.breadcrumbTermEl.querySelector('span') ?? this.breadcrumbTermEl
          span.textContent = this.state.q
            ? `Risultati di ricerca per il termine "${this.state.q}"`
            : 'Risultati di ricerca'
        }
      }

      _loadContentResults() {
        if (!this.linkResultsEl || !this.state.q) {
          if (this.linkResultsEl) this.linkResultsEl.style.display = 'none'
          return
        }

        const url = `${window.routes?.searchUrl ?? '/search'}?section_id=search-content&q=${encodeURIComponent(this.state.q)}&type=article,page`

        fetch(url, { credentials: 'same-origin' })
          .then((r) => r.text())
          .then((html) => {
            const tmp = document.createElement('div')
            tmp.innerHTML = html
            const inner = tmp.firstElementChild
            if (inner && inner.textContent.trim() !== '') {
              this.linkResultsEl.innerHTML = inner.innerHTML
              this.linkResultsEl.style.display = 'block'
            } else {
              this.linkResultsEl.style.display = 'none'
            }
          })
          .catch(() => {
            this.linkResultsEl.style.display = 'none'
          })
      }

      _setLoadingState(isLoading) {
        const section = this.closest('section')
        if (section) section.classList.toggle('loading', isLoading)
        if (this.productsGrid) this.productsGrid.classList.toggle('is-loading', isLoading)
      }

      _renderError() {
        if (this.productsGrid) {
          this.productsGrid.innerHTML = `
            <div class="empty-state" style="width:100%">
              <p class="empty-state__description">Errore nel caricamento dei prodotti. Riprova più tardi.</p>
            </div>`
        }
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — filter checkboxes (event delegation — works for dynamically rendered groups)
      // ─────────────────────────────────────────────────────────────
      _bindFilterCheckboxes() {
        // Single delegated listener on the custom element — fires for all checkboxes
        // including those rendered later by _renderFilterOptions
        this.addEventListener('change', (e) => {
          const cb = e.target.closest('.collection__filter-checkbox-list input[type="checkbox"]')
          if (!cb) return

          const key = cb.name
          const value = cb.value

          if (cb.checked) {
            if (!this.state.filters[key]) this.state.filters[key] = []
            if (!this.state.filters[key].includes(value)) this.state.filters[key].push(value)
          } else {
            this.state.filters[key] = (this.state.filters[key] ?? []).filter((v) => v !== value)
            if (!this.state.filters[key].length) delete this.state.filters[key]
          }

          this.state.page = 1
          console.log('[custom-search] checkbox change —', key, '=', value, '→ checked:', cb.checked)

          // Sync counterpart checkbox (desktop ↔ mobile panels show same filters)
          this.querySelectorAll(
            `input[type="checkbox"][name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`,
          ).forEach((el) => { if (el !== cb) el.checked = cb.checked })

          this.syncToURL()
          this.fetchResults()
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — price range (Task 5)
      // ─────────────────────────────────────────────────────────────
      _bindPriceInputs() {
        const triggerGte = (val) => {
          clearTimeout(this._priceDebounceTimer)
          this._priceDebounceTimer = setTimeout(() => {
            this.state.priceGte = val
            // Sync all paired inputs (desktop + mobile, range + number)
            this.querySelectorAll('[data-price-gte]').forEach((el) => { el.value = val })
            this.state.page = 1
            console.log('[custom-search] price gte →', val)
            this.syncToURL()
            this.fetchResults()
          }, 400)
        }

        const triggerLte = (val) => {
          clearTimeout(this._priceDebounceTimer)
          this._priceDebounceTimer = setTimeout(() => {
            this.state.priceLte = val
            this.querySelectorAll('[data-price-lte]').forEach((el) => { el.value = val })
            this.state.page = 1
            console.log('[custom-search] price lte →', val)
            this.syncToURL()
            this.fetchResults()
          }, 400)
        }

        // 'input' fires on both number inputs (typing) and range sliders (dragging)
        // 'change' fires when <price-range> custom element syncs slider → number input
        this.querySelectorAll('[data-price-gte]').forEach((el) => {
          el.addEventListener('input', () => triggerGte(el.value.trim()))
          el.addEventListener('change', () => triggerGte(el.value.trim()))
        })
        this.querySelectorAll('[data-price-lte]').forEach((el) => {
          el.addEventListener('input', () => triggerLte(el.value.trim()))
          el.addEventListener('change', () => triggerLte(el.value.trim()))
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — sort picker (Task 5)
      // ─────────────────────────────────────────────────────────────
      _bindSortPicker() {
        this.querySelectorAll('[data-cs-sortby]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const sortBy = btn.dataset.csSortby
            if (sortBy === this.state.sortBy) return

            this.state.sortBy = sortBy
            this.state.page = 1
            console.log('[custom-search] sort changed →', sortBy)

            // Update picker UI — active state
            this.querySelectorAll('[data-cs-sortby]').forEach((b) => {
              b.classList.toggle('is-selected', b.dataset.csSortby === sortBy)
            })

            // Update label
            if (this.sortLabelEl) {
              this.sortLabelEl.textContent = `Ordina: ${CS_SORT_LABELS[sortBy] ?? sortBy}`
            }

            this._closeAllPickers()
            this.syncToURL()
            this.fetchResults()
          })
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — per-page picker (Task 5)
      // ─────────────────────────────────────────────────────────────
      _bindPerPagePicker() {
        this.querySelectorAll('[data-cs-perpage]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const perPage = parseInt(btn.dataset.csPerpageValue ?? btn.dataset.csPerpage, 10)
            if (perPage === this.state.perPage) return

            this.state.perPage = perPage
            this.state.page = 1
            console.log('[custom-search] per-page changed →', perPage)

            // Update picker UI
            this.querySelectorAll('[data-cs-perpage]').forEach((b) => {
              b.classList.toggle('is-selected', parseInt(b.dataset.csPerpageValue ?? b.dataset.csPerpage, 10) === perPage)
            })

            // Update label
            if (this.perpageLabelEl) {
              this.perpageLabelEl.textContent = CS_PERPAGE_LABEL(perPage)
            }

            this._closeAllPickers()
            this.syncToURL()
            this.fetchResults()
          })
        })
      }

      // ─────────────────────────────────────────────────────────────
      // VALUE PICKER OPEN/CLOSE (Task 5)
      // ─────────────────────────────────────────────────────────────
      _bindValuePickerOpenClose() {
        // Toggle button opens the picker
        this.querySelectorAll('[data-cs-picker-toggle]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation()
            const wrapper = btn.closest('[data-cs-picker]')
            const picker = wrapper?.querySelector('.value-picker')
            if (!picker) return

            const isOpen = picker.getAttribute('aria-hidden') === 'false'
            this._closeAllPickers()

            if (!isOpen) {
              picker.setAttribute('aria-hidden', 'false')
              btn.setAttribute('aria-expanded', 'true')
              console.debug('[custom-search] picker opened:', wrapper.dataset.csPicker)
            }
          })
        })

        // Close button inside picker
        this.querySelectorAll('[data-cs-picker-close]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation()
            this._closeAllPickers()
          })
        })

        // Click outside closes pickers
        document.addEventListener('click', this._allPickersClosed)
      }

      _allPickersClosed(e) {
        if (!e.target.closest('[data-cs-picker]')) {
          this._closeAllPickers()
        }
      }

      _closeAllPickers() {
        this.querySelectorAll('.value-picker[aria-hidden="false"]').forEach((picker) => {
          picker.setAttribute('aria-hidden', 'true')
          picker.closest('[data-cs-picker]')
            ?.querySelector('[data-cs-picker-toggle]')
            ?.setAttribute('aria-expanded', 'false')
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — collapsible filter groups (event delegation)
      // ─────────────────────────────────────────────────────────────
      _bindCollapsibleToggles() {
        // Single delegated listener — fires for static price-range toggle
        // AND for dynamically rendered filter group toggles
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-cs-collapsible-toggle]')
          if (!btn) return
          const content = btn.nextElementSibling
          if (!content) return
          const isOpen = btn.getAttribute('aria-expanded') === 'true'
          btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
          content.setAttribute('aria-hidden', isOpen ? 'true' : 'false')
          content.style.height = isOpen ? '' : 'auto'
          content.style.overflow = isOpen ? '' : 'visible'
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — pagination (Task 6)
      // ─────────────────────────────────────────────────────────────
      _bindPagination() {
        this.paginationEl.addEventListener('click', (e) => {
          const item = e.target.closest('[data-page]')
          if (!item) return
          const page = parseInt(item.dataset.page, 10)
          if (page === this.state.page || item.classList.contains('is-active')) return

          this.state.page = page
          console.log('[custom-search] pagination → page', page)
          this.syncToURL()
          this.fetchResults()
          window.scroll({ top: (this.productsGrid?.offsetTop ?? 0) - 100, behavior: 'smooth' })
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — layout switcher (grid / list)
      // ─────────────────────────────────────────────────────────────
      _bindLayoutSwitcher() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="change-layout"]')
          if (!btn || btn.classList.contains('is-selected')) return

          const mode = btn.dataset.layoutMode
          this._layoutMode = mode

          // Toggle is-selected on sibling buttons
          this.querySelectorAll('[data-action="change-layout"]').forEach((b) => {
            b.classList.toggle('is-selected', b === btn)
          })

          // Update every rendered product card class
          this.querySelectorAll('.product-item').forEach((item) => {
            if (mode === 'grid') {
              item.className = 'product-item product-item--vertical 1/3--tablet-and-up 1/4--desk'
            } else {
              item.className = 'product-item product-item--list'
            }
          })

          console.log('[custom-search] layout →', mode)
        })
      }

      // ─────────────────────────────────────────────────────────────
      // EVENT BINDINGS — mobile drawer (Task 6)
      // ─────────────────────────────────────────────────────────────
      _bindMobileDrawer() {
        // Open button in toolbar
        const openBtn = this.querySelector('[data-cs-open-drawer]')
        if (openBtn) {
          openBtn.addEventListener('click', () => {
            console.log('[custom-search] mobile drawer opened')
            this._openMobileDrawer()
          })
        }

        // Close button inside drawer
        this.querySelectorAll('[data-cs-close-drawer]').forEach((btn) => {
          btn.addEventListener('click', () => {
            console.log('[custom-search] mobile drawer closed')
            this._closeMobileDrawer()
          })
        })

        // Click outside drawer closes it
        document.addEventListener('click', (e) => {
          if (
            this.mobileDrawer &&
            !this.mobileDrawer.getAttribute('aria-hidden') === false
          ) return
          if (
            this.mobileDrawer &&
            this.mobileDrawer.getAttribute('aria-hidden') === 'false' &&
            !e.target.closest('#cs-mobile-filters') &&
            !e.target.closest('[data-cs-open-drawer]')
          ) {
            this._closeMobileDrawer()
          }
        })
      }

      _openMobileDrawer() {
        if (!this.mobileDrawer) return
        this.mobileDrawer.setAttribute('aria-hidden', 'false')
        document.documentElement.classList.add('drawer-open')
      }

      _closeMobileDrawer() {
        if (!this.mobileDrawer) return
        this.mobileDrawer.setAttribute('aria-hidden', 'true')
        document.documentElement.classList.remove('drawer-open')
      }

      // ─────────────────────────────────────────────────────────────
      // STATE RESTORE HELPERS
      // ─────────────────────────────────────────────────────────────
      _restoreSortPickerState() {
        const sortBy = this.state.sortBy
        this.querySelectorAll('[data-cs-sortby]').forEach((btn) => {
          btn.classList.toggle('is-selected', btn.dataset.csSortby === sortBy)
        })
        if (this.sortLabelEl && sortBy !== 'title-ascending') {
          this.sortLabelEl.textContent = `Ordina: ${CS_SORT_LABELS[sortBy] ?? sortBy}`
        }
      }

      _restorePerPagePickerState() {
        const perPage = this.state.perPage
        this.querySelectorAll('[data-cs-perpage]').forEach((btn) => {
          btn.classList.toggle('is-selected', parseInt(btn.dataset.csPerpageValue ?? btn.dataset.csPerpage, 10) === perPage)
        })
        if (this.perpageLabelEl && perPage !== 24) {
          this.perpageLabelEl.textContent = CS_PERPAGE_LABEL(perPage)
        }
      }

      _restorePriceInputState() {
        // Defer by one frame so <price-range> custom element (from theme.js) has already run
        // its connectedCallback before we set values and CSS vars
        requestAnimationFrame(() => this._syncPriceInputs())
      }

      _syncPriceInputs() {
        this.querySelectorAll('[data-price-gte]').forEach((el) => {
          if (el.type === 'range') {
            // Range input ignores empty string → snaps to midpoint; use min instead
            el.value = this.state.priceGte !== '' ? this.state.priceGte : el.min
          } else {
            el.value = this.state.priceGte
          }
        })
        this.querySelectorAll('[data-price-lte]').forEach((el) => {
          if (el.type === 'range') {
            el.value = this.state.priceLte !== '' ? this.state.priceLte : el.max
          } else {
            el.value = this.state.priceLte
          }
        })

        // Keep CSS custom properties on each range-group in sync with thumb positions
        this.querySelectorAll('.price-range__range-group').forEach((group) => {
          const lower = group.querySelector('input[type="range"]:first-child')
          const upper = group.querySelector('input[type="range"]:last-child')
          if (lower && lower.max) {
            group.style.setProperty('--range-min', `${parseInt(lower.value) / parseInt(lower.max) * 100}%`)
          }
          if (upper && upper.max) {
            group.style.setProperty('--range-max', `${parseInt(upper.value) / parseInt(upper.max) * 100}%`)
          }
        })
      }
    },
  )
}
